/**
 * @ Author: Housefolios
 * @ Create Time: 2021-10-05 11:07:32
 * @ Modified by: David Helmick
 * @ Modified time: 2024-06-06 16:12:18
 * @ Description: Dialog for editing a portfolio
 */

import React, { useState, useEffect } from 'react'

import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  MenuItem,
  TextField,
} from '@mui/material'

import Autocomplete from '@mui/material/Autocomplete'

// Centralized lodash imports - much cleaner!
import {
  merge,
  cloneDeep,
  includes,
  isEmpty,
  filter,
  get,
  map,
  head,
} from '@/utils/lodash'

import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import { useMutation } from '@apollo/client/react'
import { UPDATE_PORTFOLIO } from '../mutations'
import { enqueueSnackbar } from 'notistack'
import { GET_PORTFOLIOS, GET_SIDEBAR_PORTFOLIOS } from '../queries'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import withSession from '@/housefolios-components/Session/withSession'
import {
  filterPreferedStrategies,
  getStrategyName,
  getStrategyOptions,
} from '@/utils/stratagies'

function EditPortfolioComponent(props) {
  const { sidebarMenu, portfolio } = props

  const [openEditPortfolio, setEditPortfolio] = useState(false)
  const handleOpenEditPortfolio = () => {
    setEditPortfolio(true)
    setName(portfolio.name)
    setDescription(portfolio.description)
    setStrategy(portfolio.strategy)
    setPreferences(
      merge(
        cloneDeep(DEFAULT_PREFERENCES),
        props?.session?.me?.activeOrg?.memberPreferences,
        props?.session?.me?.activeOrg?.preferences,
        { general: { strategies: portfolio.strategies } },
      ),
    )
  }
  const handleCloseEditPortfolio = () => {
    setEditPortfolio(false)
  }

  const [name, setName] = useState(portfolio.name)
  const [description, setDescription] = useState(
    portfolio.description,
  )
  const [strategy, setStrategy] = useState(portfolio.strategy)

  const [preferences, setPreferences] = useState(
    merge(
      cloneDeep(DEFAULT_PREFERENCES),
      props?.session?.me?.activeOrg?.memberPreferences,
      props?.session?.me?.activeOrg?.preferences,
      { general: { strategies: portfolio.strategies } },
    ),
  )

  // Never setState during render — empty preferred lists used to loop forever
  // ("Too many re-renders") when this dialog is mounted from My Properties.
  useEffect(() => {
    const preferred = filterPreferedStrategies(
      preferences,
      props?.session?.me?.activeOrg?.member,
      true,
    )
    if (!isEmpty(preferred) && !includes(preferred, strategy)) {
      setStrategy(head(preferred))
    }
  }, [preferences, strategy, props?.session?.me?.activeOrg?.member])

  const handleChangeDropdown = (event, value, collection, name) => {
    let newPreferences = { ...preferences }
    let newValue = value
    if (name === 'strategies') {
      newPreferences[collection][name] = map(
        newPreferences[collection][name],
        (preference) => {
          return {
            value: preference.value,
            active: includes(newValue, preference.value),
            memberOnly: preference.memberOnly,
          }
        },
      )
    } else newPreferences[collection][name] = newValue
    setPreferences(newPreferences)
  }

  const [updatePortfolio] = useMutation(UPDATE_PORTFOLIO)

  const stopPropagationForTab = (event) => {
    if (event.key === 'Tab') {
      event.stopPropagation()
    }
  }

  return (
    <>
      {sidebarMenu && (
        <ListItemButton onClick={handleOpenEditPortfolio}>
          <EditOutlinedIcon
            style={{ marginRight: 5, color: '#757575' }}
          />
          <span>Edit Portfolio</span>
        </ListItemButton>
      )}

      {/* {titleMenu && (
        <ListItemButton onClick={handleOpenEditPortfolio}>
          <div>
            <FontAwesomeIcon icon={['fas', 'pen']} />
          </div>
          <span>Edit Portfolio</span>
        </ListItemButton>
      )} */}
      <Dialog
        open={openEditPortfolio}
        onClose={handleCloseEditPortfolio}
        maxWidth="xs"
        fullWidth={true}
        onKeyDown={stopPropagationForTab}
      >
        <DialogTitle>Edit Portfolio</DialogTitle>
        {/* <DialogContent> */}
        <ValidatorForm
          style={{
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          onSubmit={() =>
            updatePortfolio({
              variables: {
                id: portfolio._id,
                portfolioInput: {
                  name,
                  strategy,
                  strategies: get(preferences, 'general.strategies'),
                  description,
                },
              },
              refetchQueries: [
                {
                  query: GET_PORTFOLIOS,
                },
                {
                  query: GET_SIDEBAR_PORTFOLIOS,
                },
              ],
            }).then(() => {
              enqueueSnackbar(`Changes Saved`, {
                variant: 'success',
                autoHideDuration: 3000,
              })
              handleCloseEditPortfolio()
            })
          }
        >
          <DialogContent sx={{ p: '1.5rem' }}>
            <Grid
              container
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Grid size={12}>
                <TextValidator
                  variant="outlined"
                  margin="dense"
                  label="Portfolio Name"
                  fullWidth={true}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required={true}
                  validators={['required', 'maxStringLength: 20']}
                  errorMessages={[
                    'This field is required',
                    'You cannot exceed 20 characters',
                  ]}
                />
              </Grid>
              <Grid size={12}>
                <TextValidator
                  variant="outlined"
                  margin="dense"
                  label="Portfolio Description"
                  fullWidth={true}
                  value={description}
                  onChange={(event) =>
                    setDescription(event.target.value)
                  }
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  id="checkboxes-tags-demo"
                  multiple
                  disableCloseOnSelect
                  options={filterPreferedStrategies(
                    preferences,
                    props?.session?.me?.activeOrg?.member,
                    false,
                  )}
                  value={filterPreferedStrategies(
                    preferences,
                    props?.session?.me?.activeOrg?.member,
                    true,
                  )}
                  getOptionLabel={(option) =>
                    getStrategyName(
                      option,
                      props?.session?.me?.activeOrg?.member,
                    )
                  }
                  onChange={(event, newValue) => {
                    if (!isEmpty(newValue))
                      handleChangeDropdown(
                        event,
                        newValue,
                        'general',
                        'strategies',
                      )
                  }}
                  disableClearable
                  // isOptionEqualToValue={(option, value) =>
                  //   option === value.value && value.active
                  // }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Strategies"
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <TextValidator
                  select
                  variant="outlined"
                  margin="dense"
                  label="Default Property Strategy"
                  fullWidth={true}
                  value={strategy}
                  onChange={(event) =>
                    setStrategy(event.target.value)
                  }
                  required={true}
                  validators={['required']}
                  errorMessages={['This field is required']}
                >
                  {getStrategyOptions(
                    props?.session?.me?.activeOrg?.member,
                    filterPreferedStrategies(
                      preferences,
                      props?.session?.me?.activeOrg?.member,
                      true,
                    ),
                  )}
                </TextValidator>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={handleCloseEditPortfolio}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginRight: 10 }}
            >
              Save
            </Button>
          </DialogActions>
        </ValidatorForm>
        {/* <div>
          <div>
            <div>
              <FontAwesomeIcon
                icon={['fas', 'xmark']}
              />
            </div>
          </div>
          <h4>
            Are you sure you want to delete this?
          </h4>
          <p>
            All data will be deleted. This action cannot be undone.
          </p>
          <div>
            <Button
              variant="text"
              onClick={handleCloseEditPortfolio}
            //
            >
              <span>Cancel</span>
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: '#f83145', color: 'white' }}
              onClick={handleCloseEditPortfolio}
            //
            >
              <span>Delete</span>
            </Button>
          </div>
        </div> */}
      </Dialog>
    </>
  )
}

export default withSession(EditPortfolioComponent)
